import { useCallback, useEffect, useMemo, useState } from 'react'
import { Dialog } from '../dialogs/Dialog'
import { Button } from '../common/Button'
import { DatumZeitFeld, DATUM_UNGUELTIG_HINWEIS } from '../forms/DatumFelder'

/** Phasenzeiten eines Einsatzes (API-Namen) mit Anzeige-Text, in der Reihenfolge des Ablaufs */
export const PHASEN_ZEITEN = [
  { feld: 'alarmierungszeit', label: 'Alarmierung' },
  { feld: 'abfahrt_zeit', label: 'Abfahrt Wache' },
  { feld: 'ankunft_zeit', label: 'Ankunft Einsatzort' },
  { feld: 'einsatzende_zeit', label: 'Einsatzende' },
  { feld: 'einsatzbereit_zeit', label: 'Einsatzbereitschaft wiederhergestellt' },
] as const
type ZeitFeld = typeof PHASEN_ZEITEN[number]['feld']

/** Phasenzeiten je Typ: Übungen (UEBUNG) mit Übungstexten und ohne Abfahrt Wache */
const UEBUNG_ZEIT_LABEL: Record<string, string> = {
  alarmierungszeit: 'Übungsbereitschaft', ankunft_zeit: 'Ankunft am Übungsort', einsatzende_zeit: 'Übungsende', einsatzbereit_zeit: 'Einsatzbereitschaft wiederhergestellt',
}
export const phasenZeitenFuer = (typ?: string | null): readonly { feld: ZeitFeld; label: string }[] =>
  typ === 'UEBUNG' ? PHASEN_ZEITEN.filter(p => p.feld !== 'abfahrt_zeit').map(p => ({ feld: p.feld, label: UEBUNG_ZEIT_LABEL[p.feld] ?? p.label })) : PHASEN_ZEITEN

export interface PhasenZeitenApi {
  get: (url: string) => Promise<{ data: any }>
  put: (url: string, body: any) => Promise<{ data: any }>
}

interface ProtokollEintrag { id: number; feld: string; feld_name: string; alter_wert: string | null; neuer_wert: string | null; nutzer: string | null; geaendert_am: string }

/** Zeiten sind „Berliner Ortszeit ohne Zeitzone“ (so speichert Pilot-Hub2 alle Zeiten) und werden unverändert angezeigt */
const zeitText = (z?: string | null) => z ? new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'
const eingabeWert = (z?: string | null) => (z ? z.slice(0, 16) : '')
/** Zweitname derselben Zeit (die DB führt jede Phasenzeit doppelt); Admin-Einsätze liefern beide Spalten */
const ALIAS: Record<string, string> = { abfahrt_zeit: 'abfahrt_wache', ankunft_zeit: 'ankunft_einsatzort', einsatzende_zeit: 'einsatzende', einsatzbereit_zeit: 'einsatzbereit' }
export const phasenZeitWert = (e: Record<string, any>, feld: string): string | null => e[feld] ?? e[ALIAS[feld]] ?? null

/** Weiche Hinweise zur Reihenfolge (Alarmierung ≤ Abfahrt ≤ Ankunft ≤ Einsatzende ≤ Einsatzbereitschaft wiederhergestellt); sperren nichts */
export function reihenfolgeHinweise(werte: Partial<Record<ZeitFeld, string | null | undefined>>, typ?: string | null): string[] {
  const gesetzt = phasenZeitenFuer(typ).map(p => ({ ...p, t: werte[p.feld] ? new Date(werte[p.feld] as string).getTime() : NaN })).filter(p => !Number.isNaN(p.t))
  const h: string[] = []
  for (let i = 1; i < gesetzt.length; i++) if (gesetzt[i].t < gesetzt[i - 1].t) h.push(`${gesetzt[i].label} liegt vor ${gesetzt[i - 1].label}.`)
  return h
}

/** Änderungsprotokoll der Phasenzeiten (alt, neu, Person, Zeitpunkt); lädt bei jeder Änderung von `version` neu */
export function ZeitenProtokoll({ einsatzId, api, version = 0 }: { einsatzId: number; api: PhasenZeitenApi; version?: number }) {
  const [liste, setListe] = useState<ProtokollEintrag[] | null>(null)
  const [fehler, setFehler] = useState('')
  useEffect(() => {
    let lebt = true
    api.get(`/einsaetze/${einsatzId}/zeiten-protokoll`)
      .then(r => { if (lebt) { setListe(Array.isArray(r.data) ? r.data : []); setFehler('') } })
      .catch(() => { if (lebt) setFehler('Das Protokoll konnte nicht geladen werden.') })
    return () => { lebt = false }
  }, [api, einsatzId, version])
  if (fehler) return <p className="text-xs text-red-300">{fehler}</p>
  if (!liste) return <p className="text-xs text-dark-text-muted">Protokoll wird geladen …</p>
  if (!liste.length) return <p className="text-xs text-dark-text-subtle" data-testid="zeiten-protokoll-leer">Keine nachträglichen Änderungen der Phasenzeiten.</p>
  return (
    <ul className="divide-y divide-dark-border overflow-hidden rounded-lg border border-dark-border text-sm" data-testid="zeiten-protokoll">
      {liste.map(p => (
        <li key={p.id} className="space-y-0.5 px-3 py-2">
          <div className="font-medium text-dark-text">{p.feld_name}: <span className="text-dark-text-muted">{zeitText(p.alter_wert)}</span> → <span>{zeitText(p.neuer_wert)}</span></div>
          <div className="text-xs text-dark-text-muted">{p.nutzer ?? 'Unbekannt'} · {zeitText(p.geaendert_am)}</div>
        </li>
      ))}
    </ul>
  )
}

/**
 * Dialog „Phasenzeiten bearbeiten“ (Führung und Admin, auch nach Abschluss des Einsatzes). Gespeichert werden nur geänderte Zeiten
 * (PUT /einsaetze/{id}/zeiten); das Leeren einer Zeit ist erlaubt. Der Einsatzstatus und die Drohnenbindung bleiben unverändert.
 * Unlogische Reihenfolgen zeigen nur einen Hinweis.
 */
export function PhasenZeitenDialog({ einsatz, api, onClose, onGespeichert }: {
  einsatz: Record<string, any>
  api: PhasenZeitenApi
  onClose: () => void
  onGespeichert?: () => void
}) {
  const zeitenListe = phasenZeitenFuer(einsatz.typ)
  const start = useMemo(() => PHASEN_ZEITEN.reduce((acc, p) => { acc[p.feld] = eingabeWert(phasenZeitWert(einsatz, p.feld)); return acc }, {} as Record<ZeitFeld, string>), [einsatz])
  const [werte, setWerte] = useState<Record<ZeitFeld, string>>(start)
  const [ungueltig, setUngueltig] = useState<Record<string, boolean>>({})
  const hatUngueltig = Object.values(ungueltig).some(Boolean)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState('')
  const [serverHinweise, setServerHinweise] = useState<string[]>([])
  const [version, setVersion] = useState(0)
  const geaendert = zeitenListe.filter(p => werte[p.feld] !== start[p.feld])
  const hinweise = reihenfolgeHinweise(werte, einsatz.typ)

  const speichern = useCallback(async () => {
    setSpeichert(true); setFehler('')
    try {
      const body: Record<string, string | null> = {}
      geaendert.forEach(p => { body[p.feld] = werte[p.feld] || null })
      const r = await api.put(`/einsaetze/${einsatz.id}/zeiten`, body)
      setServerHinweise(Array.isArray(r.data?.hinweise) ? r.data.hinweise : [])
      setVersion(v => v + 1)
      onGespeichert?.()
      if (!(r.data?.hinweise ?? []).length) onClose()
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setFehler(typeof d === 'string' ? d : 'Die Zeiten konnten nicht gespeichert werden.')
    } finally { setSpeichert(false) }
  }, [api, einsatz.id, geaendert, werte, onGespeichert, onClose])

  const gespeichert = serverHinweise.length > 0 && geaendert.length === 0
  return (
    <Dialog isOpen onClose={onClose} title="Phasenzeiten bearbeiten" maxWidth="xl"
      footer={(
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} className="min-h-[48px]">{gespeichert ? 'Schließen' : 'Abbrechen'}</Button>
          <Button onClick={speichern} disabled={speichert || geaendert.length === 0 || hatUngueltig} className="min-h-[48px]" data-testid="zeiten-speichern">{speichert ? 'Speichern …' : 'Speichern'}</Button>
        </div>
      )}>
      <div className="space-y-4" data-testid="zeiten-dialog">
        <p className="text-sm text-dark-text-muted">Jede Änderung wird mit altem Wert, neuem Wert, Person und Zeitpunkt protokolliert. Der Einsatzstatus bleibt unverändert. Eine Zeit lässt sich auch leeren.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {zeitenListe.map(p => (
            <div key={p.feld} className="min-w-0">
              <label className="label" htmlFor={`pz-${p.feld}`}>{p.label}</label>
              <div className="flex gap-2">
                <DatumZeitFeld id={`pz-${p.feld}`} value={werte[p.feld]} onChange={v => setWerte(w => ({ ...w, [p.feld]: v }))} onUngueltig={u => setUngueltig(x => ({ ...x, [p.feld]: u }))} className="min-h-[48px] w-full" />
                <button type="button" aria-label={`${p.label} leeren`} disabled={!werte[p.feld] && !ungueltig[p.feld]} onClick={() => { setWerte({ ...werte, [p.feld]: '' }); setUngueltig(x => ({ ...x, [p.feld]: false })) }}
                  className="min-h-[48px] shrink-0 rounded-lg border border-dark-border px-3 text-sm text-dark-text-muted disabled:opacity-40">Leeren</button>
              </div>
            </div>
          ))}
        </div>
        {hatUngueltig && <p className="text-sm text-red-300" role="alert" data-testid="datum-ungueltig-hinweis">{DATUM_UNGUELTIG_HINWEIS}</p>}
        {hinweise.length > 0 && <ul className="list-disc rounded-lg border border-amber-500/50 bg-amber-500/10 py-2 pl-7 pr-3 text-sm text-amber-200" role="status" data-testid="zeiten-hinweis">{hinweise.map(h => <li key={h}>{h}</li>)}</ul>}
        {serverHinweise.length > 0 && <p className="rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-dark-text" role="status">Gespeichert. Hinweis: {serverHinweise.join(' ')}</p>}
        {fehler && <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" role="alert">{fehler}</p>}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-dark-text">Änderungsprotokoll</h4>
          <ZeitenProtokoll einsatzId={einsatz.id} api={api} version={version} />
        </div>
      </div>
    </Dialog>
  )
}

/** Block für die Detailansicht eines Einsatzes: Phasenzeiten, Knopf zum nachträglichen Ändern und das Änderungsprotokoll */
/** nurLesen: Übungen (UEBUNG) haben keine Phasenleiste; vorhandene Zeiten alter Übungen bleiben lesbar, Bearbeiten entfällt (ohne Zeiten wird nichts angezeigt) */
export function PhasenZeitenBlock({ einsatz, api, onGeaendert, nurLesen }: { einsatz: Record<string, any>; api: PhasenZeitenApi; onGeaendert?: () => void; nurLesen?: boolean }) {
  const [offen, setOffen] = useState(false)
  const [version, setVersion] = useState(0)
  if (nurLesen && !phasenZeitenFuer(einsatz.typ).some(p => phasenZeitWert(einsatz, p.feld))) return null
  return (
    <div className="space-y-3 border-t border-dark-border pt-4" data-testid="phasenzeiten-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-dark-text">Phasenzeiten</h3>
        {!nurLesen && <button type="button" onClick={() => setOffen(true)} data-testid="zeiten-bearbeiten"
          className="inline-flex min-h-[44px] items-center rounded-lg border border-dark-border px-3 text-sm text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text">Zeiten bearbeiten</button>}
      </div>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {phasenZeitenFuer(einsatz.typ).map(p => (
          <div key={p.feld} className="flex justify-between gap-3"><dt className="text-dark-text-muted">{p.label}:</dt><dd data-testid={`zeit-${p.feld}`}>{zeitText(phasenZeitWert(einsatz, p.feld))}</dd></div>
        ))}
      </dl>
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">Änderungsprotokoll</h4>
        <ZeitenProtokoll einsatzId={einsatz.id} api={api} version={version} />
      </div>
      {offen && <PhasenZeitenDialog einsatz={einsatz} api={api} onClose={() => setOffen(false)} onGespeichert={() => { setVersion(v => v + 1); onGeaendert?.() }} />}
    </div>
  )
}
